import { useSyncExternalStore } from 'react';

/**
 * Whether the signed-in person is a view-only member of the workspace they
 * have open (role 'viewer', the Firm plan's free read-only seat).
 *
 * WorkspaceProvider owns the answer and publishes it here, so the parts of the
 * app shell that sit outside the provider (the Assist launcher and the
 * command bar, AuthenticatedAppChrome) hide their write and AI actions too.
 * Inside the provider read useIsViewOnly() from '@/hooks/useCanEdit'.
 */
let viewOnly = false;
const listeners = new Set<() => void>();

export function setViewOnlySession(next: boolean): void {
  if (viewOnly === next) return;
  viewOnly = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The current answer, for code outside React (the edge-function wrapper). */
export function isViewOnlySessionActive(): boolean {
  return viewOnly;
}

/** What the server says when it refuses a view-only member (code view_only). */
export const VIEW_ONLY_MESSAGE =
  'You have view-only access to this workspace. Ask an owner or admin to make this change.';

export function useViewOnlySession(): boolean {
  return useSyncExternalStore(subscribe, () => viewOnly, () => false);
}

/**
 * The screens a view-only member uses: people, companies, deals and the
 * pipeline (whose toolbar holds reporting and the team view). Every other
 * view sends them back to People.
 */
export const VIEW_ONLY_CRM_SUBVIEWS: ReadonlySet<string> = new Set([
  'people', 'contacts', 'contact', 'companies', 'company', 'pipelines', 'pipeline', 'deal',
]);

export const VIEW_ONLY_HOME_SUBVIEW = 'people';

export function isViewOnlyAllowedView(activeView: string, crmSubView: string): boolean {
  return activeView === 'CRM' && VIEW_ONLY_CRM_SUBVIEWS.has(crmSubView);
}
